<template>
  <AppLayout>
    <div class="transaction">
      <section id="transaction-products">
        <div class="container">
          <h6 class="fw-bold" style="margin-bottom:0;">Manage Services</h6>

          <!-- Search + Add + Manage Categories/Units -->
          <div class="d-flex align-items-center flex-wrap gap-2 my-3">
            <input
              type="text"
              class="form-control form-control-sm"
              style="max-width:300px;"
              placeholder="Search services..."
              v-model="searchQuery"
              @input="debouncedSearch"
            />
            <button class="btn btn-sm btn-primary" @click="openCreate">
              <i class="fas fa-plus me-1"></i> Add Service
            </button>
            <button class="btn btn-sm btn-style-2" @click="showCategoryModal = true">
              <i class="fas fa-tags me-1"></i> Categories
            </button>
            <button class="btn btn-sm btn-style-2" @click="showUnitModal = true">
              <i class="fas fa-ruler me-1"></i> Units
            </button>
          </div>

          <!-- Table -->
          <div class="products-list transaction-products-list" style="margin-top:0;">
            <SkeletonLoader v-if="loading" type="table" :rows="6" :columns="8" />
            <table v-else class="table table-hover" style="font-size:11px;">
              <thead style="background:#f8f9fa;border-bottom:2px solid #dee2e6;">
                <tr>
                  <th>No.</th>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Category</th>
                  <th>Unit</th>
                  <th>Unit Price</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody v-if="services.length > 0">
                <tr v-for="(svc, idx) in services" :key="svc.id">
                  <td>{{ idx + 1 }}</td>
                  <td>{{ svc.name }}</td>
                  <td><code>{{ svc.code || '—' }}</code></td>
                  <td>{{ svc.category?.name || '—' }}</td>
                  <td>{{ svc.unit?.name || '—' }}</td>
                  <td>{{ svc.unitPrice != null ? Number(svc.unitPrice).toFixed(2) : '—' }}</td>
                  <td class="text-muted" style="max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{{ svc.description || '—' }}</td>
                  <td>
                    <div class="btn-group btn-group-sm">
                      <button class="btn btn-sm btn-style-2" @click="openEdit(svc)">Edit</button>
                      <button class="btn btn-sm btn-outline-danger" @click="removeService(svc)">Delete</button>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr><td colspan="8" class="text-center py-4 text-muted">No services found.</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>

    <!-- Create / Edit Service Modal -->
    <div v-if="showModal" class="svc-modal-overlay" @click.self="closeModal">
      <div class="svc-modal-box">
        <div class="svc-modal-header">
          <h6 class="fw-bold mb-0">{{ editingService ? 'Edit Service' : 'Add Service' }}</h6>
          <button class="btn-close-plain" @click="closeModal">&times;</button>
        </div>
        <div class="svc-modal-body">
          <div class="row g-2">
            <div class="col-md-6">
              <label class="form-label" style="font-size:11px;">Name *</label>
              <input type="text" class="form-control form-control-sm" v-model="form.name" placeholder="e.g. System Integration" />
            </div>
            <div class="col-md-6">
              <label class="form-label" style="font-size:11px;">Code (optional)</label>
              <input type="text" class="form-control form-control-sm" v-model="form.code" placeholder="e.g. SVC-001" :disabled="!!editingService" />
            </div>
            <div class="col-md-6">
              <label class="form-label" style="font-size:11px;">Category</label>
              <select class="form-control form-control-sm" v-model="form.categoryId">
                <option :value="null">— None —</option>
                <option v-for="c in serviceCategories" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label" style="font-size:11px;">Unit</label>
              <select class="form-control form-control-sm" v-model="form.unitId">
                <option :value="null">— None —</option>
                <option v-for="u in serviceUnits" :key="u.id" :value="u.id">{{ u.name }}{{ u.symbol ? ` (${u.symbol})` : '' }}</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label" style="font-size:11px;">Unit Price</label>
              <input type="number" class="form-control form-control-sm" v-model="form.unitPrice" placeholder="0.00" min="0" step="0.01" />
            </div>
            <div class="col-12">
              <label class="form-label" style="font-size:11px;">Description</label>
              <textarea class="form-control form-control-sm" rows="3" v-model="form.description" placeholder="Service description..."></textarea>
            </div>
          </div>
          <p v-if="formError" class="text-danger small mt-2">{{ formError }}</p>
        </div>
        <div class="svc-modal-footer">
          <button class="btn btn-sm btn-style-2" @click="closeModal">Cancel</button>
          <button class="btn btn-sm btn-primary" :disabled="saving" @click="saveService">
            <span v-if="saving"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
            <span v-else>Save</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Categories Modal -->
    <div v-if="showCategoryModal" class="svc-modal-overlay" @click.self="showCategoryModal = false">
      <div class="svc-modal-box" style="max-width:480px;">
        <div class="svc-modal-header">
          <h6 class="fw-bold mb-0">Service Categories</h6>
          <button class="btn-close-plain" @click="showCategoryModal = false">&times;</button>
        </div>
        <div class="svc-modal-body">
          <div class="d-flex gap-2 mb-3">
            <input type="text" class="form-control form-control-sm" v-model="newCategoryName" placeholder="New category name" @keyup.enter="addCategory" />
            <button class="btn btn-sm btn-primary" @click="addCategory" :disabled="!newCategoryName.trim() || savingCategory">Add</button>
          </div>
          <ul class="list-group list-group-flush">
            <li v-for="c in serviceCategories" :key="c.id" class="list-group-item d-flex justify-content-between align-items-center py-2">
              <span>{{ c.name }}</span>
              <button class="btn btn-sm btn-outline-danger" @click="removeCategory(c)">Delete</button>
            </li>
            <li v-if="serviceCategories.length === 0" class="list-group-item text-muted">No categories yet.</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Units Modal -->
    <div v-if="showUnitModal" class="svc-modal-overlay" @click.self="showUnitModal = false">
      <div class="svc-modal-box" style="max-width:480px;">
        <div class="svc-modal-header">
          <h6 class="fw-bold mb-0">Service Units</h6>
          <button class="btn-close-plain" @click="showUnitModal = false">&times;</button>
        </div>
        <div class="svc-modal-body">
          <div class="d-flex gap-2 mb-3 flex-wrap">
            <input type="text" class="form-control form-control-sm" style="flex:1;min-width:120px;" v-model="newUnitName" placeholder="Unit name" @keyup.enter="addUnit" />
            <input type="text" class="form-control form-control-sm" style="width:80px;" v-model="newUnitSymbol" placeholder="Symbol" @keyup.enter="addUnit" />
            <button class="btn btn-sm btn-primary" @click="addUnit" :disabled="!newUnitName.trim() || savingUnit">Add</button>
          </div>
          <ul class="list-group list-group-flush">
            <li v-for="u in serviceUnits" :key="u.id" class="list-group-item d-flex justify-content-between align-items-center py-2">
              <span>{{ u.name }}{{ u.symbol ? ` (${u.symbol})` : '' }}</span>
              <button class="btn btn-sm btn-outline-danger" @click="removeUnit(u)">Delete</button>
            </li>
            <li v-if="serviceUnits.length === 0" class="list-group-item text-muted">No units yet.</li>
          </ul>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import AppLayout from '@/components/AppLayout.vue'
import SkeletonLoader from '@/components/SkeletonLoader.vue'

const API_BASE = (process.env.VUE_APP_URL || 'http://localhost:3000').replace(/\/+$/, '')

function getToken() {
  try { return JSON.parse(localStorage.getItem('access_token')) } catch (e) { return localStorage.getItem('access_token') }
}

function authHeaders() {
  const token = getToken()
  return { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' }
}

function debounce(fn, ms) {
  let t
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms) }
}

export default {
  name: 'ServicesSettings',
  components: { AppLayout, SkeletonLoader },
  setup() {
    const services = ref([])
    const serviceCategories = ref([])
    const serviceUnits = ref([])
    const loading = ref(false)
    const searchQuery = ref('')
    const showModal = ref(false)
    const showCategoryModal = ref(false)
    const showUnitModal = ref(false)
    const editingService = ref(null)
    const saving = ref(false)
    const savingCategory = ref(false)
    const savingUnit = ref(false)
    const formError = ref('')
    const newCategoryName = ref('')
    const newUnitName = ref('')
    const newUnitSymbol = ref('')

    const form = reactive({ name: '', code: '', categoryId: null, unitId: null, unitPrice: '', description: '' })

    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_BASE}/service-categories`, { headers: authHeaders() })
        const data = await res.json()
        serviceCategories.value = Array.isArray(data.data) ? data.data : []
      } catch (e) {
        serviceCategories.value = []
      }
    }

    const fetchUnits = async () => {
      try {
        const res = await fetch(`${API_BASE}/service-units`, { headers: authHeaders() })
        const data = await res.json()
        serviceUnits.value = Array.isArray(data.data) ? data.data : []
      } catch (e) {
        serviceUnits.value = []
      }
    }

    const fetchServices = async () => {
      loading.value = true
      try {
        const q = searchQuery.value ? `?search=${encodeURIComponent(searchQuery.value)}` : ''
        const res = await fetch(`${API_BASE}/services${q}`, { headers: authHeaders() })
        const data = await res.json()
        services.value = Array.isArray(data.data) ? data.data : []
      } catch (e) {
        services.value = []
      } finally {
        loading.value = false
      }
    }

    const debouncedSearch = debounce(fetchServices, 400)

    const openCreate = () => {
      editingService.value = null
      Object.assign(form, { name: '', code: '', categoryId: null, unitId: null, unitPrice: '', description: '' })
      formError.value = ''
      showModal.value = true
    }

    const openEdit = (svc) => {
      editingService.value = svc
      Object.assign(form, {
        name: svc.name,
        code: svc.code || '',
        categoryId: svc.categoryId ?? svc.category?.id ?? null,
        unitId: svc.unitId ?? svc.unit?.id ?? null,
        unitPrice: svc.unitPrice ?? '',
        description: svc.description || '',
      })
      formError.value = ''
      showModal.value = true
    }

    const closeModal = () => { showModal.value = false; editingService.value = null }

    const saveService = async () => {
      if (!form.name.trim()) { formError.value = 'Name is required.'; return }
      formError.value = ''
      saving.value = true
      try {
        const payload = {
          name: form.name.trim(),
          code: form.code?.trim() || undefined,
          categoryId: form.categoryId || undefined,
          unitId: form.unitId || undefined,
          unitPrice: form.unitPrice !== '' ? Number(form.unitPrice) : undefined,
          description: form.description.trim() || undefined,
        }
        let res
        if (editingService.value) {
          res = await fetch(`${API_BASE}/services/${editingService.value.id}`, {
            method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload)
          })
        } else {
          res = await fetch(`${API_BASE}/services`, {
            method: 'POST', headers: authHeaders(), body: JSON.stringify(payload)
          })
        }
        const data = await res.json()
        if (!res.ok) throw new Error(data?.message || 'Failed to save service')
        closeModal()
        await fetchServices()
      } catch (err) {
        formError.value = err.message
      } finally {
        saving.value = false
      }
    }

    const removeService = async (svc) => {
      if (!confirm(`Delete service "${svc.name}"?`)) return
      try {
        const res = await fetch(`${API_BASE}/services/${svc.id}`, { method: 'DELETE', headers: authHeaders() })
        if (!res.ok) { const d = await res.json(); throw new Error(d?.message || 'Failed') }
        await fetchServices()
      } catch (err) {
        alert(err.message)
      }
    }

    const addCategory = async () => {
      if (!newCategoryName.value.trim()) return
      savingCategory.value = true
      try {
        const res = await fetch(`${API_BASE}/service-categories`, {
          method: 'POST', headers: authHeaders(), body: JSON.stringify({ name: newCategoryName.value.trim() })
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data?.message || 'Failed')
        newCategoryName.value = ''
        await fetchCategories()
      } catch (err) {
        alert(err.message)
      } finally {
        savingCategory.value = false
      }
    }

    const removeCategory = async (c) => {
      if (!confirm(`Delete category "${c.name}"?`)) return
      try {
        const res = await fetch(`${API_BASE}/service-categories/${c.id}`, { method: 'DELETE', headers: authHeaders() })
        if (!res.ok) { const d = await res.json(); throw new Error(d?.message || 'Failed') }
        await fetchCategories()
      } catch (err) {
        alert(err.message)
      }
    }

    const addUnit = async () => {
      if (!newUnitName.value.trim()) return
      savingUnit.value = true
      try {
        const res = await fetch(`${API_BASE}/service-units`, {
          method: 'POST', headers: authHeaders(), body: JSON.stringify({ name: newUnitName.value.trim(), symbol: newUnitSymbol.value.trim() || undefined })
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data?.message || 'Failed')
        newUnitName.value = ''
        newUnitSymbol.value = ''
        await fetchUnits()
      } catch (err) {
        alert(err.message)
      } finally {
        savingUnit.value = false
      }
    }

    const removeUnit = async (u) => {
      if (!confirm(`Delete unit "${u.name}"?`)) return
      try {
        const res = await fetch(`${API_BASE}/service-units/${u.id}`, { method: 'DELETE', headers: authHeaders() })
        if (!res.ok) { const d = await res.json(); throw new Error(d?.message || 'Failed') }
        await fetchUnits()
      } catch (err) {
        alert(err.message)
      }
    }

    onMounted(() => {
      fetchServices()
      fetchCategories()
      fetchUnits()
    })

    return {
      services, serviceCategories, serviceUnits, loading, searchQuery,
      showModal, showCategoryModal, showUnitModal, editingService,
      saving, savingCategory, savingUnit, formError,
      newCategoryName, newUnitName, newUnitSymbol,
      form,
      fetchServices, debouncedSearch,
      openCreate, openEdit, closeModal, saveService, removeService,
      addCategory, removeCategory, addUnit, removeUnit,
    }
  },
}
</script>

<style scoped>
.svc-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
}

.svc-modal-box {
  background: #fff;
  border-radius: 8px;
  width: 100%;
  max-width: 560px;
  box-shadow: 0 8px 32px rgba(0,0,0,.18);
  display: flex;
  flex-direction: column;
}

.svc-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 18px;
  border-bottom: 1px solid #e9ecef;
}

.btn-close-plain {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #6c757d;
  line-height: 1;
}

.svc-modal-body {
  padding: 16px 18px;
  overflow-y: auto;
}

.svc-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 11px 18px;
  border-top: 1px solid #e9ecef;
}
</style>
